import { FC, Key, useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate, useParams, useResolvedPath } from 'react-router-dom';
import { SelectableList, SelectableListDataType } from '../selectable-list/selectable-list';
import { ComponentQueryLoader } from '../component-query-loader';
import { SharedArchivedCaseListContainerArchivedCasesByApplicantIdDocument } from '../../../generated';
import { Empty } from 'antd';
import { DATE_TIME_FORMAT, FormatDate } from '../../../constants';

interface ArchivedCaseListCommonContainerProps {
  isVerticalColumn: boolean | undefined;
  applicantId: string;
  onIsArchivedCase?: (isArchived: boolean, archivedCaseId: string) => void;
}

const formatCamelCase = (camelCaseString: string) => {
  return camelCaseString
    .replace(/caseWorker/g, 'Caseworker')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (str) => str.toUpperCase());
};

export const ArchivedCaseListCommonContainer: FC<ArchivedCaseListCommonContainerProps> = (props) => {
  const location = useLocation();
  const [selectedArchivedCase, setSelectedArchivedCase] = useState<SelectableListDataType | undefined>();
  const { data, loading, error } = useQuery(SharedArchivedCaseListContainerArchivedCasesByApplicantIdDocument, {
    variables: {
      id: props.applicantId
    },
    context: {
      headers: {
        'cache-enabled': 'true'
      }
    },
    skip: !props.applicantId
  });

  const navigate = useNavigate();
  const formattedData: SelectableListDataType[] =
    data?.archivedCasesByApplicantId
      ?.map((item) => ({
        key: item?.id,
        initials: item?.caseName?.substring(0, 2).toUpperCase() || '',
        title: item?.caseName ?? '',
        timestamp: FormatDate(item?.archivedAt, DATE_TIME_FORMAT),
        progress: formatCamelCase(item?.state ?? ''),
        caseType: item?.caseType,
        schemaVersion: '',
        state: item?.state ?? ''
      }))
      ?.sort((a, b) => (a.timestamp > b.timestamp ? -1 : 1)) || [];

  const currentCaseId = useParams().caseId;
  const currentRoutePath = useResolvedPath('').pathname;
  const pathSegments = currentRoutePath.split('/');
  const casesIndex = pathSegments.indexOf('cases');
  const newLocation = pathSegments.slice(0, casesIndex + 1).join('/');

  useEffect(() => {
    if (currentCaseId && formattedData.length > 0 && selectedArchivedCase === undefined) {
      const selectedCase = formattedData.find((r) => r.key === currentCaseId);
      if (selectedCase) {
        setSelectedArchivedCase(selectedCase);
      }
    }
  }, [currentCaseId, formattedData, selectedArchivedCase]);

  useEffect(() => {
    setSelectedArchivedCase(undefined);
  }, [location])
  
  const getNavigationPath = (id: string, caseType: string) => {
    const path = props.isVerticalColumn ? `${currentRoutePath}/cases/${id}/case-type/${caseType}/archived` : `${newLocation}/${id}/case-type/${caseType}/archived`;
    return path;
  };

  const onAnArchivedCaseSelected = (selectedRowKey: Key, caseType: string) => {
    const selectedCase = formattedData.find((r) => r.key === selectedRowKey);
    setSelectedArchivedCase(selectedCase);
    const newUrl = getNavigationPath(selectedRowKey.toString(), caseType);
    navigate(newUrl, { replace: true });
  };

  return (
    <ComponentQueryLoader
      loading={loading}
      error={error}
      hasData={formattedData.length > 0}
      hasDataComponent={
        <SelectableList isVerticalColumn={props.isVerticalColumn} data={formattedData} selectedRecord={selectedArchivedCase} onSelectChange={onAnArchivedCaseSelected} />
      }
      noDataComponent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />}
    />
  );
};
